/**
 * The loading overlay's content (reference 04), from the LevelService's real
 * load progress (rule 7): the step under way, as one line, and an overall
 * fraction for the progress bar.
 */
import type { LoadProgress, LoadStage } from "../levels/LevelService";

/** The step under way, with its count when it has one ("Meshes · textures 3/9"); "" between steps. */
export function currentStep(progress: LoadProgress): string {
  const active = progress.stages.find((stage) => stage.status === "active");
  if (!active) return "";
  return active.detail ? `${active.label} ${active.detail}` : active.label;
}

/** 0..1: each stage counts equally, the active one by its own progress. */
export function transitionFraction(progress: LoadProgress): number {
  if (progress.phase === "done") return 1;
  if (progress.stages.length === 0) return 0;

  const total = progress.stages.reduce((sum, stage) => sum + stageFraction(stage, progress), 0);
  return total / progress.stages.length;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function stageFraction(stage: LoadStage, progress: LoadProgress): number {
  switch (stage.status) {
    case "done":
      return 1;
    case "queued":
    case "failed":
      return 0;
    case "active":
      return activeFraction(stage, progress);
  }
}

/** Counted progress ("11/17"), or streamed bytes while the manifest itself downloads. */
function activeFraction(stage: LoadStage, progress: LoadProgress): number {
  const counted = stage.detail?.match(/^(\d+)\/(\d+)$/);
  if (counted) {
    const [, done = "0", total = "0"] = counted;
    return Number(total) === 0 ? 1 : Number(done) / Number(total);
  }
  if (stage.id === "entities" && progress.bytesTotal) {
    return Math.min(1, progress.bytesLoaded / progress.bytesTotal);
  }
  return 0;
}
